import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Landmark, Plus, Loader2, FileText, Printer, Download, Trash2, Pencil, ShieldCheck, ChevronDown, Gift,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = () => ({
  banker_name: "",
  banker_title: "Private Banker & Trust Officer",
  institution_name: "",
  institution_type: "Private Bank & Trust Institution",
  institution_address: "",
  jurisdiction: "",
  registration_no: "",
  email: "",
  phone: "",
  effective_date: "",
  powers: "",
});

const Field = ({ label, value, onChange, type = "text", area, ph, testid }) => (
  <div className="mb-3">
    <label className="block text-[10.5px] uppercase tracking-[0.1em] text-[#8fae9f] mb-1">{label}</label>
    {area ? (
      <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={ph}
        className="w-full bg-[#0F1A14] border border-[#1f4030] rounded-lg px-3 py-2 text-[#E7F3EE] text-[13px] resize-y" />
    ) : (
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={ph}
        className="w-full bg-[#0F1A14] border border-[#1f4030] rounded-lg px-3 py-2 text-[#E7F3EE] text-[13px]" />
    )}
  </div>
);

const DOC_OPTIONS = [
  { id: "full", name: "Full Master Charter Package" },
  { id: "charter", name: "Master Charter" },
  { id: "association", name: "Articles of Association" },
  { id: "pledge", name: "Collateral Pledge" },
  { id: "treasury", name: "Treasury Authorization" },
  { id: "certificate", name: "Certificate of Authority" },
];

export default function PrivateBankCharter() {
  const nav = useNavigate();
  const token = localStorage.getItem("th_admin_token");
  const cfg = { headers: { Authorization: `Bearer ${token}` } };

  const [bankers, setBankers] = useState([]);
  const [reserve, setReserve] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [masterAccount, setMasterAccount] = useState(null);
  const [f, setF] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/private-bank/bankers`, cfg);
      setBankers(r.data.bankers || []);
      try { const rr = await axios.get(`${API}/admin/private-bank/reserve`, cfg); setReserve(rr.data.reserve); setLedger(rr.data.ledger || []); setMasterAccount(rr.data.master_account || null); } catch { /* ignore */ }
    } catch (e) {
      toast.error("Failed to load registered bankers");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.banker_name.trim() || !f.institution_name.trim()) {
      toast.error("Banker name and institution name are required");
      return;
    }
    setBusy(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/private-bank/bankers/${editingId}`, f, cfg);
        toast.success("Banker record updated");
      } else {
        const r = await axios.post(`${API}/admin/private-bank/bankers`, f, cfg);
        toast.success(`Registered · Charter No. ${r.data.charter_no}`);
      }
      setF(emptyForm());
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  const edit = (b) => {
    setEditingId(b.id);
    setF({
      banker_name: b.banker_name || "", banker_title: b.banker_title || "",
      institution_name: b.institution_name || "", institution_type: b.institution_type || "",
      institution_address: b.institution_address || "", jurisdiction: b.jurisdiction || "",
      registration_no: b.registration_no || "", email: b.email || "", phone: b.phone || "",
      effective_date: b.effective_date || "", powers: b.powers || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (b) => {
    if (!window.confirm(`Delete the charter record for ${b.institution_name}?`)) return;
    try {
      await axios.delete(`${API}/admin/private-bank/bankers/${b.id}`, cfg);
      toast.success("Record deleted");
      await load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const openDoc = async (b, doc, mode) => {
    try {
      const r = await axios.get(`${API}/admin/private-bank/bankers/${b.id}/package.pdf?doc=${doc}`,
        { ...cfg, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      if (mode === "download") {
        const a = document.createElement("a");
        a.href = url;
        a.download = `master-charter-${(b.institution_name || "member").replace(/\s+/g, "_")}-${doc}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        const w = window.open(url, "_blank");
        if (w && mode === "print") w.addEventListener("load", () => w.print());
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Could not generate the document");
    }
  };

  const money = (n) => Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

  const waive = async (b, waiveIt) => {
    try {
      await axios.post(`${API}/admin/private-bank/bankers/${b.id}/waive`, { waive: waiveIt }, cfg);
      toast.success(waiveIt ? "Banker activated — membership active & Treasury Prime account provisioned" : "Waiver removed");
      await load();
    } catch (e) {
      toast.error("Could not update the subscription");
    }
  };

  const [creditAmt, setCreditAmt] = useState({});
  const [creditBusy, setCreditBusy] = useState("");
  const setupCredit = async (b) => {
    const amt = Number(String(creditAmt[b.id] || "").replace(/[^0-9.]/g, ""));
    if (!amt || amt <= 0) { toast.error("Enter a line-of-credit amount (USD)"); return; }
    setCreditBusy(b.id);
    try {
      const r = await axios.post(`${API}/admin/private-bank/bankers/${b.id}/credit`, { amount: amt, currency: "USD" }, cfg);
      toast.success(`Line approved → Certificate of Deposit ${r.data.banker?.credit_line?.cd_no || ""} issued (${money(amt)})`);
      setCreditAmt((p) => ({ ...p, [b.id]: "" }));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not set up the line of credit");
    } finally { setCreditBusy(""); }
  };

  const openInstrument = async (b, instrumentId, mode) => {
    try {
      const r = await axios.get(`${API}/admin/private-bank/bankers/${b.id}/instrument.pdf?instrument_id=${instrumentId}`,
        { ...cfg, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      if (mode === "download") {
        const a = document.createElement("a"); a.href = url; a.download = `instrument-${instrumentId}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      } else {
        const w = window.open(url, "_blank");
        if (w && mode === "print") w.addEventListener("load", () => w.print());
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Could not open the instrument");
    }
  };

  const openTreasury = async (b, txId, mode) => {
    try {
      const r = await axios.get(`${API}/admin/private-bank/bankers/${b.id}/treasury.pdf?tx_id=${txId}`,
        { ...cfg, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      if (mode === "download") {
        const a = document.createElement("a"); a.href = url; a.download = `treasury-${txId}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      } else {
        window.open(url, "_blank");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Could not open the transaction");
    }
  };

  return (
    <div className="min-h-screen bg-[#07120D] text-[#E7F3EE]" data-testid="private-bank-page">
      <header className="px-6 sm:px-10 py-4 flex items-center justify-between border-b border-[#143026] bg-[#050D09]">
        <div className="flex items-center gap-3">
          <Landmark className="text-[#E4C97A]" size={20} />
          <div>
            <div className="text-[9px] tracking-[0.24em] uppercase text-[#E4C97A] font-bold">Restricted · Back Office</div>
            <h1 className="text-lg font-serif">Bank of International Settlements and Securities</h1>
          </div>
        </div>
        <button onClick={() => nav("/admin")} data-testid="pb-back-btn"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold text-[#E7F3EE] border border-[#1f4030] hover:border-[#E4C97A] px-4 py-2 rounded-full transition-colors">
          <ArrowLeft size={13} /> Admin
        </button>
      </header>

      <main className="max-w-[1100px] mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Registration */}
        <section className="bg-[#0C1812] border border-[#143026] rounded-2xl p-6" data-testid="pb-register-card">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={16} className="text-[#E4C97A]" />
            <h2 className="font-serif text-[18px]">{editingId ? "Edit Banker / Institution" : "Register Private Banker / Trust Institution"}</h2>
          </div>
          <p className="text-[12px] text-[#7fa08f] mb-5">
            Register a member institution and its private banker. On save the system issues a Membership No., Charter No.,
            and Certificate of Authority No. — then you can print or download the full Master Charter package for
            commercial banking assets and transactions.
          </p>
          <div className="grid sm:grid-cols-2 gap-x-6">
            <Field label="Private Banker — Full Name" value={f.banker_name} onChange={set("banker_name")} ph="e.g. Jonathan P. Reed" testid="pb-banker-name" />
            <Field label="Banker Title" value={f.banker_title} onChange={set("banker_title")} testid="pb-banker-title" />
            <Field label="Institution Name" value={f.institution_name} onChange={set("institution_name")} ph="e.g. Meridian Private Trust & Bank" testid="pb-institution-name" />
            <Field label="Institution Type" value={f.institution_type} onChange={set("institution_type")} testid="pb-institution-type" />
            <Field label="Principal Office Address" value={f.institution_address} onChange={set("institution_address")} ph="Street, City, State ZIP, Country" testid="pb-institution-address" />
            <Field label="Governing Jurisdiction" value={f.jurisdiction} onChange={set("jurisdiction")} ph="e.g. Delaware, United States" testid="pb-jurisdiction" />
            <Field label="Registration / EIN No." value={f.registration_no} onChange={set("registration_no")} testid="pb-registration-no" />
            <Field label="Effective Date" value={f.effective_date} onChange={set("effective_date")} type="date" testid="pb-effective-date" />
            <Field label="Email" value={f.email} onChange={set("email")} type="email" testid="pb-email" />
            <Field label="Phone" value={f.phone} onChange={set("phone")} testid="pb-phone" />
          </div>
          <Field label="Additional Authorized Powers (optional)" value={f.powers} onChange={set("powers")} area
            ph="Any extra chartered powers to recite in the Master Charter" testid="pb-powers" />
          <div className="flex items-center gap-3 mt-2">
            <button onClick={submit} disabled={busy} data-testid="pb-submit-btn"
              className="inline-flex items-center gap-2 bg-[#E4C97A] hover:bg-[#d4b964] disabled:opacity-60 text-[#06160F] font-bold text-[12px] uppercase tracking-[0.1em] px-5 py-2.5 rounded-lg transition-colors">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {editingId ? "Save Changes" : "Register & Issue Charter"}
            </button>
            {editingId && (
              <button onClick={() => { setEditingId(null); setF(emptyForm()); }} data-testid="pb-cancel-edit"
                className="text-[12px] text-[#7fa08f] hover:text-[#E7F3EE] underline">Cancel edit</button>
            )}
          </div>
        </section>

        {/* Registered list */}
        <section data-testid="pb-list">
          {reserve && (
            <div className="bg-[#0C1812] border border-[#143026] rounded-2xl p-5 mb-5" data-testid="pb-reserve-dashboard">
              <h2 className="font-serif text-[16px] text-[#F4ECCF] mb-3">Association Master Reserve Account</h2>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
                {[
                  ["Reserve Total", reserve.total, "#cfe3d8"],
                  ["Allocated (CDs)", reserve.allocated, "#E4C97A"],
                  ["Available", reserve.available, "#6FD3AE"],
                  ["Disbursed", reserve.disbursed, "#cfe3d8"],
                  ["Fees Collected", reserve.fees_collected, "#6FD3AE"],
                ].map(([label, val, color]) => (
                  <div key={label} className="bg-[#0F1A14] border border-[#1f4030] rounded-xl p-3">
                    <div className="text-[9.5px] uppercase tracking-[0.1em] text-[#7fa08f]">{label}</div>
                    <div className="font-serif text-[15px] mt-1" style={{ color }}>${Number(val || 0).toLocaleString("en-US")}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 bg-[#0A241A] border border-[#14503B] rounded-xl p-4" data-testid="pb-master-account">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-serif text-[13px] text-[#6FD3AE]">Master Account — Treasury Prime</h3>
                  {masterAccount && masterAccount.status === "open"
                    ? <span className="text-[9px] uppercase tracking-[0.12em] text-[#6FD3AE] bg-[#0F2A1E] border border-[#14503B] px-2 py-0.5 rounded-full">Open</span>
                    : <span className="text-[9px] uppercase tracking-[0.12em] text-[#E4C97A]">Provisioning…</span>}
                </div>
                {masterAccount && masterAccount.account_number ? (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <div className="text-[9.5px] uppercase tracking-[0.1em] text-[#7fa08f]">Account Number</div>
                      <div className="font-mono text-[15px] text-[#E7F3EE] mt-0.5" data-testid="pb-master-account-number">{masterAccount.account_number}</div>
                    </div>
                    <div>
                      <div className="text-[9.5px] uppercase tracking-[0.1em] text-[#7fa08f]">Routing Number (ABA)</div>
                      <div className="font-mono text-[15px] text-[#E7F3EE] mt-0.5" data-testid="pb-master-routing-number">{masterAccount.routing_number}</div>
                    </div>
                  </div>
                ) : (
                  <p className="text-[11px] text-[#7fa08f] mt-2">The Association master bank account is being provisioned via Treasury Prime. Refresh in a moment.</p>
                )}
              </div>
              {ledger.length > 0 && (
                <div className="mt-5" data-testid="pb-reserve-ledger">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-serif text-[13px] text-[#cfe3d8]">Master Reserve Ledger</h3>
                    <span className="text-[10px] uppercase tracking-[0.1em] text-[#7fa08f]">{ledger.length} entries</span>
                  </div>
                  <div className="overflow-x-auto border border-[#1f4030] rounded-xl max-h-[380px] overflow-y-auto">
                    <table className="w-full text-[11px]">
                      <thead className="sticky top-0 bg-[#0F1A14] text-[#7fa08f] uppercase tracking-[0.08em] text-[9.5px]">
                        <tr>
                          <th className="text-left px-3 py-2">Date</th>
                          <th className="text-left px-3 py-2">Kind</th>
                          <th className="text-left px-3 py-2">Memo</th>
                          <th className="text-right px-3 py-2">Credit</th>
                          <th className="text-right px-3 py-2">Debit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledger.map((e) => {
                          const amt = Number(e.amount || 0);
                          const isCredit = e.direction === "credit";
                          const isDebit = e.direction === "debit";
                          return (
                            <tr key={e.id} data-testid={`pb-ledger-row-${e.id}`} className="border-t border-[#143026] text-[#cfe3d8]">
                              <td className="px-3 py-1.5 whitespace-nowrap text-[#9fc0af]">{e.created_at ? new Date(e.created_at).toLocaleString() : "—"}</td>
                              <td className="px-3 py-1.5 whitespace-nowrap"><span className="text-[9px] uppercase tracking-[0.08em] text-[#6FD3AE]">{e.kind}</span></td>
                              <td className="px-3 py-1.5 text-[#cfe3d8]">{e.memo || "—"}</td>
                              <td className="px-3 py-1.5 text-right font-mono text-[#6FD3AE]">{isCredit && amt ? `$${amt.toLocaleString("en-US")}` : ""}</td>
                              <td className="px-3 py-1.5 text-right font-mono text-[#E4C97A]">{isDebit && amt ? `$${amt.toLocaleString("en-US")}` : ""}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          <h2 className="font-serif text-[18px] mb-4">Registered Bankers & Institutions</h2>
          {loading ? (
            <div className="flex items-center gap-2 text-[#7fa08f] text-sm"><Loader2 size={16} className="animate-spin" /> Loading…</div>
          ) : bankers.length === 0 ? (
            <div className="bg-[#0C1812] border border-dashed border-[#1f4030] rounded-xl p-8 text-center text-[#7fa08f] text-sm" data-testid="pb-empty">
              No bankers registered yet. Register one above to issue a Master Charter.
            </div>
          ) : (
            <div className="space-y-4">
              {bankers.map((b) => (
                <div key={b.id} data-testid={`pb-card-${b.id}`}
                  className="bg-[#0C1812] border border-[#143026] rounded-xl p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="font-serif text-[16px] text-[#F4ECCF]">{b.institution_name}</div>
                      <div className="text-[12px] text-[#9fc0af]">{b.banker_name} · {b.banker_title}</div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-[#7fa08f]">
                        <span>Membership: <b className="text-[#cfe3d8]">{b.membership_no}</b></span>
                        <span>Charter: <b className="text-[#cfe3d8]">{b.charter_no}</b></span>
                        <span>Cert: <b className="text-[#cfe3d8]">{b.certificate_no}</b></span>
                        {b.registration_no ? <span>Reg/EIN: <b className="text-[#cfe3d8]">{b.registration_no}</b></span> : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        {b.active ? (
                          <span data-testid={`pb-sub-active-${b.id}`} className="inline-flex items-center gap-1 text-[10.5px] font-bold text-[#06160F] bg-[#6FD3AE] px-2.5 py-1 rounded-full">
                            {b.subscription?.waived ? "Fee waived · active" : "Subscribed · $499/mo"}
                          </span>
                        ) : (
                          <span data-testid={`pb-sub-inactive-${b.id}`} className="inline-flex items-center gap-1 text-[10.5px] font-bold text-[#E4C97A] border border-[#8A6D1F] px-2.5 py-1 rounded-full">
                            Inactive
                          </span>
                        )}
                        {b.subscription?.status === "pending_cashapp" && !b.active ? (
                          <span data-testid={`pb-cashapp-pending-${b.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold text-[#00D632] border border-[#00D632]/50 px-2.5 py-1 rounded-full">Cash App payment pending</span>
                        ) : null}
                        {b.channel === "public" ? <span className="text-[10px] text-[#7fa08f] uppercase tracking-[0.1em]">Self-registered</span> : null}
                        {b.subscription?.waived ? (
                          <button onClick={() => waive(b, false)} data-testid={`pb-unwaive-${b.id}`}
                            className="text-[10.5px] text-[#9fc0af] border border-[#1f4030] hover:border-[#E4C97A] px-2.5 py-1 rounded-md transition-colors">Remove waiver</button>
                        ) : (
                          <button onClick={() => waive(b, true)} data-testid={`pb-waive-${b.id}`}
                            className="inline-flex items-center gap-1 text-[10.5px] font-bold text-[#06160F] bg-[#E4C97A] hover:bg-[#d4b964] px-2.5 py-1 rounded-md transition-colors">
                            <Gift size={12} /> {b.subscription?.status === "pending_cashapp" ? "Activate (Cash App paid)" : "Activate Banker (No Charge)"}
                          </button>
                        )}
                      </div>
                      {b.active && !(b.credit_line && b.credit_line.cd_no) ? (
                        <div className="flex flex-wrap items-center gap-2 mt-3 bg-[#0F1A14] border border-[#1f4030] rounded-lg px-3 py-2" data-testid={`pb-setup-credit-${b.id}`}>
                          <span className="text-[10px] uppercase tracking-[0.12em] text-[#6f8f7e]">Set up line of credit → CD</span>
                          <input value={creditAmt[b.id] || ""} onChange={(e) => setCreditAmt((p) => ({ ...p, [b.id]: e.target.value }))}
                            placeholder="Amount (USD), e.g. 2500000" data-testid={`pb-credit-amount-${b.id}`}
                            className="flex-1 min-w-[200px] max-w-xs bg-[#0C1812] border border-[#1f4030] rounded-md px-3 py-1.5 text-[#E7F3EE] text-[12px]" />
                          <button onClick={() => setupCredit(b)} disabled={creditBusy === b.id} data-testid={`pb-approve-credit-${b.id}`}
                            className="inline-flex items-center gap-1 text-[10.5px] font-bold text-[#06160F] bg-[#6FD3AE] hover:bg-[#54c097] disabled:opacity-60 px-3 py-1.5 rounded-md transition-colors">
                            <Plus size={12} /> {creditBusy === b.id ? "Approving…" : "Approve Line → Issue CD"}
                          </button>
                        </div>
                      ) : null}
                      {b.credit_line?.cd_no || (b.tp_account && b.tp_account.account_number) ? (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[10.5px] text-[#7fa08f]" data-testid={`pb-bankinfo-${b.id}`}>
                          {b.tp_account?.account_number ? <span>Bank Acct: <b className="text-[#cfe3d8] font-mono">{"••••" + String(b.tp_account.account_number).slice(-4)}</b></span> : null}
                          {b.tp_account?.routing_number ? <span>Routing: <b className="text-[#cfe3d8] font-mono">{b.tp_account.routing_number}</b></span> : null}
                          {b.credit_line?.cd_no ? <span>CD: <b className="text-[#cfe3d8]">{b.credit_line.cd_no}</b></span> : null}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => openDoc(b, "full", "print")} data-testid={`pb-print-${b.id}`}
                        className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#06160F] bg-[#6FD3AE] hover:bg-[#54c097] px-3 py-2 rounded-md transition-colors">
                        <Printer size={13} /> Print
                      </button>
                      <button onClick={() => openDoc(b, "full", "download")} data-testid={`pb-download-${b.id}`}
                        className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#06160F] bg-[#E4C97A] hover:bg-[#d4b964] px-3 py-2 rounded-md transition-colors">
                        <Download size={13} /> Download
                      </button>
                      <button onClick={() => edit(b)} data-testid={`pb-edit-${b.id}`}
                        className="inline-flex items-center gap-1.5 text-[11px] text-[#9fc0af] border border-[#1f4030] hover:border-[#E4C97A] px-3 py-2 rounded-md transition-colors">
                        <Pencil size={13} /> Edit
                      </button>
                      <button onClick={() => del(b)} data-testid={`pb-delete-${b.id}`}
                        className="inline-flex items-center gap-1.5 text-[11px] text-[#e59aa0] border border-[#4a2327] hover:border-[#e5484d] px-3 py-2 rounded-md transition-colors">
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-[#143026] flex flex-wrap items-center gap-2">
                    <span className="text-[10.5px] uppercase tracking-[0.12em] text-[#7fa08f] mr-1">Individual documents:</span>
                    {DOC_OPTIONS.filter((d) => d.id !== "full").map((d) => (
                      <div key={d.id} className="inline-flex rounded-md overflow-hidden border border-[#1f4030]">
                        <button onClick={() => openDoc(b, d.id, "view")} data-testid={`pb-doc-view-${d.id}-${b.id}`}
                          className="inline-flex items-center gap-1 text-[10.5px] text-[#cfe3d8] hover:bg-[#12271d] px-2.5 py-1.5 transition-colors">
                          <FileText size={11} /> {d.name}
                        </button>
                        <button onClick={() => openDoc(b, d.id, "download")} data-testid={`pb-doc-dl-${d.id}-${b.id}`}
                          className="inline-flex items-center text-[#9fc0af] hover:bg-[#12271d] px-2 py-1.5 border-l border-[#1f4030] transition-colors" title="Download">
                          <Download size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                  {(b.credit_line && b.credit_line.status === "approved") && (
                    <div className="mt-3 pt-3 border-t border-[#143026]" data-testid={`pb-credit-${b.id}`}>
                      <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-[#7fa08f]">
                        <span>Securities Line: <b className="text-[#cfe3d8]">{money(b.credit_line.approved_amount)}</b></span>
                        <span>Used: <b className="text-[#E4C97A]">{money(b.credit_line.used_amount)}</b></span>
                        <span>Available: <b className="text-[#6FD3AE]">{money(b.credit_line.available_amount)}</b></span>
                        <span>Facility: <b className="text-[#cfe3d8]">{b.credit_line.facility_no}</b></span>
                      </div>
                    </div>
                  )}
                  {(b.instruments && b.instruments.length > 0) && (
                    <div className="mt-3 pt-3 border-t border-[#143026]" data-testid={`pb-instruments-${b.id}`}>
                      <div className="text-[10.5px] uppercase tracking-[0.12em] text-[#7fa08f] mb-2">Issued instruments ({b.instruments.length})</div>
                      <div className="space-y-2">
                        {b.instruments.map((i) => (
                          <div key={i.id} data-testid={`pb-instr-${i.id}`} className="flex flex-wrap items-center justify-between gap-2 bg-[#0F1A14] border border-[#1f4030] rounded-md px-3 py-2">
                            <div className="text-[11.5px]">
                              <span className="text-[#F4ECCF] font-semibold">{i.type_name} · {money(i.amount)}</span>
                              <span className="text-[#7fa08f]"> · No. {i.number}{i.payee ? ` · ${i.payee}` : ""}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <button onClick={() => openInstrument(b, i.id, "view")} data-testid={`pb-instr-view-${i.id}`}
                                className="inline-flex items-center gap-1 text-[10.5px] text-[#cfe3d8] border border-[#1f4030] hover:border-[#E4C97A] px-2.5 py-1 rounded-md transition-colors"><FileText size={11} /> View</button>
                              <button onClick={() => openInstrument(b, i.id, "download")} data-testid={`pb-instr-dl-${i.id}`}
                                className="inline-flex items-center text-[#9fc0af] border border-[#1f4030] hover:border-[#E4C97A] px-2 py-1 rounded-md transition-colors" title="Download"><Download size={11} /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {(b.treasury_transactions && b.treasury_transactions.length > 0) && (
                    <div className="mt-3 pt-3 border-t border-[#143026]" data-testid={`pb-treasury-${b.id}`}>
                      <div className="text-[10.5px] uppercase tracking-[0.12em] text-[#7fa08f] mb-2">Treasury transactions ({b.treasury_transactions.length})</div>
                      <div className="space-y-2">
                        {b.treasury_transactions.map((t) => (
                          <div key={t.id} data-testid={`pb-tx-${t.id}`} className="flex flex-wrap items-center justify-between gap-2 bg-[#0F1A14] border border-[#1f4030] rounded-md px-3 py-2">
                            <div className="text-[11.5px]">
                              <span className="text-[#F4ECCF] font-semibold">{t.action === "buy" ? "BUY" : "SELL"} · {t.security_name} · {money(t.principal)}</span>
                              <span className="text-[#7fa08f]"> · No. {t.number} · CUSIP {t.cusip}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <button onClick={() => openTreasury(b, t.id, "view")} data-testid={`pb-tx-view-${t.id}`}
                                className="inline-flex items-center gap-1 text-[10.5px] text-[#cfe3d8] border border-[#1f4030] hover:border-[#E4C97A] px-2.5 py-1 rounded-md transition-colors"><FileText size={11} /> View</button>
                              <button onClick={() => openTreasury(b, t.id, "download")} data-testid={`pb-tx-dl-${t.id}`}
                                className="inline-flex items-center text-[#9fc0af] border border-[#1f4030] hover:border-[#E4C97A] px-2 py-1 rounded-md transition-colors" title="Download"><Download size={11} /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <p className="text-center text-[11px] text-[#5e7a6c] leading-relaxed pb-8">
          Private association charter. Not a government-issued bank license and not FDIC/NCUA/SIPC insured.
        </p>
      </main>
    </div>
  );
}
